import * as Sentry from '@sentry/browser';
import React, { Component, useEffect } from 'react';
import { Router } from 'react-router-dom';
import { Global } from '@emotion/react';
import { globalCSS } from './App.globalCSS';
// import LogRocket from 'logrocket';
// import setupLogRocketReact from 'logrocket-react';
import { App as AntdApp, ConfigProvider } from 'antd';
import { getAntdTheme } from './antdTheme';
import { history } from './~reusables/util';
import { AppFieldsProvider } from './~reusables/contexts/AppFieldsContext';
import { ThemeContextProvider } from './~reusables/contexts/ThemeContext';
import {
  ThemeModeProvider,
  useThemeMode,
} from './~reusables/contexts/ThemeModeContext';
import { InjectStore, store, $globalLoading } from './store';
import {
  initializeOnUserAuth,
  unsubscribeOnUserAuth,
} from './~reusables/actions';
import WebAppV2 from './pages/WebAppV2';
import { LoadingBar } from './components/atoms/LoadingBar';
import { TransitionProvider } from './~reusables/contexts/TransitionContext';
import { IUser } from '@todesktop/shared';
import { ErrorInfo } from 'react';

// if (process.env.NODE_ENV === 'production') {
//   setupLogRocketReact(LogRocket);
//   LogRocket.init('to-desktop/web-app');
// }

const ThemedConfigProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { resolvedTheme } = useThemeMode();
  // Static methods (message, Modal.confirm) render outside this tree, so they
  // need the theme passed to them as well.
  useEffect(() => {
    const theme = getAntdTheme(resolvedTheme);
    ConfigProvider.config({
      holderRender: (node) => (
        <ConfigProvider theme={theme}>{node}</ConfigProvider>
      ),
    });
  }, [resolvedTheme]);
  return (
    <ConfigProvider theme={getAntdTheme(resolvedTheme)}>
      {children}
    </ConfigProvider>
  );
};

interface IAppState {
  user: IUser;
  userDataLoaded: boolean;
  globalLoading: boolean;
}

class App extends Component {
  componentDidMount() {
    initializeOnUserAuth();
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // this.setState({ error }); - error state is not used
    if (store.getRawState().user?.isImpersonator) return;
    Sentry.withScope((scope) => {
      scope.setExtra('componentStack', errorInfo.componentStack);
      Sentry.captureException(error);
      // const eventId = Sentry.captureException(error);
      // this.setState({ eventId }); - eventId state is not used
    });
  }

  componentWillUnmount() {
    unsubscribeOnUserAuth();
  }

  public render() {
    return (
      <ThemeModeProvider>
        <ThemeContextProvider>
          <ThemedConfigProvider>
            <AntdApp>
              <Global styles={globalCSS} />
              <InjectStore
                on={(state) => ({
                  user: state.user,
                  userDataLoaded: state.userDataLoaded,
                  globalLoading: $globalLoading(state),
                })}
              >
                {({ globalLoading }: IAppState) => (
                  <>
                    <LoadingBar isLoading={globalLoading} />
                    <Router history={history}>
                      <TransitionProvider>
                        <AppFieldsProvider>
                          <WebAppV2 />
                        </AppFieldsProvider>
                      </TransitionProvider>
                    </Router>
                  </>
                )}
              </InjectStore>
            </AntdApp>
          </ThemedConfigProvider>
        </ThemeContextProvider>
      </ThemeModeProvider>
    );
  }
}

export default App;
